import React, { useEffect, useState, useMemo, useRef } from "react";
import { Music, X, Minimize2, Loader2, SkipForward, Plus, Upload } from "lucide-react";
import * as AdminAPI from '@/lib/admin-api';

type MusicPlayerDockProps = {
  side?: "left" | "right";
  forceOpenSignal?: number;
  zIndex?: number;
  onFocus?: () => void;
};

const MusicPlayerDock: React.FC<MusicPlayerDockProps> = ({ side = "right", forceOpenSignal = 0, zIndex = 9900, onFocus }) => {
  const [isDocked, setIsDocked] = useState(true);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<string | null>(null);
  const [playlists, setPlaylists] = useState<Array<{id: string; name: string; embedUrl: string; description?: string; source: 'admin' | 'user'}>>([]);
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: '', artist: '', embedCode: '' });
  const [uploadLoading, setUploadLoading] = useState(false);
  const iframeContainerRef = useRef<HTMLDivElement>(null);

  // Log state changes for debugging
  useEffect(() => {
    console.log('MusicPlayer isDocked:', isDocked);
  }, [isDocked]);

  useEffect(() => {
    if (forceOpenSignal > 0 && isDocked) {
      setIsDocked(false);
    }
  }, [forceOpenSignal]);

  // Load music from database
  useEffect(() => {
    const loadMusic = async () => {
      setLoading(true);
      try {
        const songs = await AdminAPI.getAllSongs();
        if (songs.length > 0) {
          const dbPlaylists = songs.map(song => ({
            id: song.id,
            name: `${song.title} - ${song.artist}`,
            embedUrl: song.url,
            description: `${song.artist}`,
            source: classifySource(song.cover, song.id),
          }));
          setPlaylists(dbPlaylists);
          setSelectedPlaylistId(dbPlaylists[0].id);
        }
      } catch (error) {
        console.error('Failed to load music:', error);
      } finally {
        setLoading(false);
      }
    };
    loadMusic();
  }, []);

  // Handle upload
  const handleUpload = async () => {
    if (!uploadForm.title || !uploadForm.artist || !uploadForm.embedCode) {
      alert('Please fill in all fields');
      return;
    }

    setUploadLoading(true);
    try {
      const id = (globalThis.crypto?.randomUUID && globalThis.crypto.randomUUID()) || `song_${Date.now()}`;
      const normalizedEmbed = normalizeEmbed(uploadForm.embedCode.trim());

      await AdminAPI.createSong({
        id,
        title: uploadForm.title,
        artist: uploadForm.artist,
        url: normalizedEmbed,
        cover: 'user-upload',
      });
      
      // Reload playlist
      const songs = await AdminAPI.getAllSongs();
      const dbPlaylists = songs.map(song => ({
        id: song.id,
        name: `${song.title} - ${song.artist}`,
        embedUrl: song.url,
        description: `${song.artist}`,
        source: classifySource(song.cover, song.id),
      }));
      setPlaylists(dbPlaylists);
      
      // Reset form and close modal
      setUploadForm({ title: '', artist: '', embedCode: '' });
      setShowUploadModal(false);
      alert('Music uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload music:', error);
      alert('Failed to upload music. Please try again.');
    } finally {
      setUploadLoading(false);
    }
  };

  // ✅ Update iframe content when playlist changes (WITHOUT remounting)
  useEffect(() => {
    if (iframeContainerRef.current && currentPlaylist) {
      // Add autoplay parameter to embed URL if it's an iframe
      let embedCode = currentPlaylist.embedUrl;
      
      // If it's an iframe, modify the src to include autoplay
      if (embedCode.includes('<iframe')) {
        // Add autoplay=1 and allow autoplay attribute to YouTube URLs
        if (embedCode.includes('youtube.com') || embedCode.includes('youtu.be')) {
          embedCode = embedCode.replace(/src="([^"]+)"/, (match, url) => {
            const separator = url.includes('?') ? '&' : '?';
            return `src="${url}${separator}autoplay=1&mute=0"`;
          });
          // Add allow attribute for autoplay
          if (!embedCode.includes('allow=')) {
            embedCode = embedCode.replace(/<iframe/, '<iframe allow="autoplay; encrypted-media"');
          }
        }
        // Add auto_play=true to SoundCloud URLs
        else if (embedCode.includes('soundcloud.com')) {
          embedCode = embedCode.replace(/src="([^"]+)"/, (match, url) => {
            const separator = url.includes('?') ? '&' : '?';
            return `src="${url}${separator}auto_play=true"`;
          });
          if (!embedCode.includes('allow=')) {
            embedCode = embedCode.replace(/<iframe/, '<iframe allow="autoplay"');
          }
        }
        // Add autoplay=1 to Spotify URLs
        else if (embedCode.includes('spotify.com')) {
          embedCode = embedCode.replace(/src="([^"]+)"/, (match, url) => {
            const separator = url.includes('?') ? '&' : '?';
            return `src="${url}${separator}autoplay=1"`;
          });
          if (!embedCode.includes('allow=')) {
            embedCode = embedCode.replace(/<iframe/, '<iframe allow="autoplay; encrypted-media"');
          }
        }
      }
      
      iframeContainerRef.current.innerHTML = embedCode;
      
      // Force reload iframe after a brief moment to trigger autoplay
      setTimeout(() => {
        const iframe = iframeContainerRef.current?.querySelector('iframe');
        if (iframe && iframe.src) {
          const currentSrc = iframe.src;
          iframe.src = '';
          setTimeout(() => {
            iframe.src = currentSrc;
          }, 50);
        }
      }, 100);
    }
  }, [selectedPlaylistId, playlists]);

  const currentPlaylist = useMemo(
    () => playlists.find(p => p.id === selectedPlaylistId),
    [selectedPlaylistId, playlists]
  );

  // Swap grouping labels per request: Admin list shows admin seeds; Your Uploads shows user-created
  // Note: Some legacy records have ambiguous source; invert grouping so admin seeds stay under Admin and new uploads under Your Uploads
  const adminPlaylists = useMemo(() => playlists.filter(p => p.source !== 'user'), [playlists]);
  const userPlaylists = useMemo(() => playlists.filter(p => p.source === 'user'), [playlists]);

  function classifySource(cover: string | undefined | null, id: string) {
    const value = (cover || '').toLowerCase();
    if (value.includes('user-upload') || value === 'user') return 'user';
    // Heuristic: locally created ids from this UI start with song_ and should be user
    if (id.startsWith('song_')) return 'user';
    return 'admin';
  }

  // Normalize embed code or raw URLs into iframe HTML
  function normalizeEmbed(raw: string) {
    const trimmed = raw.trim();
    const isIframe = /<iframe/i.test(trimmed);

    // If already iframe, return as-is
    if (isIframe) return trimmed;

    // Basic wrappers for common providers
    const makeIframe = (src: string, allow: string) =>
      `<iframe src="${src}" width="100%" height="220" frameborder="0" allow="${allow}" allowfullscreen></iframe>`;

    // YouTube
    if (/youtube\.com|youtu\.be/.test(trimmed)) {
      const src = trimmed.replace('watch?v=', 'embed/');
      return makeIframe(src, 'autoplay; encrypted-media');
    }

    // Spotify
    if (/open\.spotify\.com/.test(trimmed)) {
      const src = trimmed.replace('/track/', '/embed/track/');
      return makeIframe(src, 'autoplay; encrypted-media');
    }

    // SoundCloud
    if (/soundcloud\.com/.test(trimmed)) {
      const src = `https://w.soundcloud.com/player/?url=${encodeURIComponent(trimmed)}&auto_play=true`;
      return makeIframe(src, 'autoplay');
    }

    // Fallback: wrap any URL in a basic iframe
    return makeIframe(trimmed, 'autoplay');
  }

  // Function to go to next playlist
  const handleNext = () => {
    if (playlists.length === 0) return;
    const currentIndex = playlists.findIndex(p => p.id === selectedPlaylistId);
    const nextIndex = (currentIndex + 1) % playlists.length;
    setSelectedPlaylistId(playlists[nextIndex].id);
  };

  return (
    <>
      {/* Docked State - Tab on Side */}
      {isDocked && (
        <div
          onClick={() => { setIsDocked(false); onFocus?.(); }}
          className="fixed right-0 top-[20%] -translate-y-1/2 transition-all duration-500 cursor-pointer group translate-x-[calc(100%-2rem)] hover:translate-x-0"
          style={{ zIndex: zIndex }}
        >
          <div className="backdrop-blur-xl bg-gradient-to-r from-orange-600 to-red-600 rounded-l-2xl p-4 pl-3 shadow-[0_0_20px_rgba(234,88,12,0.5)] border-l border-t border-b border-orange-400">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Music className="w-6 h-6 text-white animate-pulse" />
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-yellow-400 rounded-full animate-ping" />
              </div>
              <span className="text-white font-black whitespace-nowrap tracking-wider">OPEN PLAYER</span>
            </div>
          </div>
        </div>
      )}

      {/* Full Player - Slides off-screen but NEVER unmounts */}
      <div 
        onMouseDown={() => onFocus?.()}
        className="fixed top-6 right-6 transition-all duration-300"
        style={{ 
          transform: isDocked ? 'translateX(150%)' : 'translateX(0)',
          opacity: 1,
          pointerEvents: isDocked ? 'none' : 'auto',
          zIndex: zIndex
        }}
      >
        <div className="backdrop-blur-xl bg-gradient-to-br from-gray-900 via-gray-800 to-black rounded-2xl border-3 border-orange-500/60 shadow-2xl w-80 max-w-[90vw] relative overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-600 via-yellow-500 to-orange-600 p-2 flex items-center justify-between border-b-3 border-orange-700">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="w-6 h-6 rounded-full bg-gray-900/50 border-2 border-orange-400 flex items-center justify-center flex-shrink-0">
                <div className="w-3 h-3 rounded-full bg-gradient-to-br from-orange-500 to-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-black text-xs tracking-wider drop-shadow-lg truncate">🎵 WHIPT MUSIC VIBES</h3>
                <p className="text-orange-100 text-[9px] font-bold truncate">
                  {currentPlaylist ? currentPlaylist.name : "INSERT CASSETTE"}
                </p>
              </div>
            </div>
            <div className="flex gap-1 flex-shrink-0 items-center">
              <button
                onClick={() => setShowUploadModal(true)}
                className="text-white bg-green-600 hover:bg-green-500 h-8 w-8 rounded-lg border-2 border-green-400 flex items-center justify-center shadow-lg"
                title="Add Music"
              >
                <Plus className="w-4 h-4" />
              </button>
              <button
                onClick={handleNext}
                className="text-white bg-orange-600 hover:bg-orange-500 h-8 w-8 rounded-lg border-2 border-yellow-400 flex items-center justify-center shadow-lg disabled:opacity-30 disabled:cursor-not-allowed"
                title="Next track"
                disabled={playlists.length <= 1}
              >
                <SkipForward className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsDocked(true)}
                className="text-white hover:bg-orange-600/50 h-7 w-7 rounded-lg border border-orange-300/50 flex items-center justify-center"
                title="Dock to side (music keeps playing)"
              >
                <Minimize2 className="w-3 h-3" />
              </button>
              <button
                onClick={() => setIsDocked(true)}
                className="text-white hover:bg-orange-600/50 h-7 w-7 rounded-lg border border-orange-300/50 flex items-center justify-center"
                title="Close player"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Music Player */}
          <div className="p-3 bg-gradient-to-b from-gray-900 to-black">
            {/* Playlist Selector */}
            {playlists.length > 0 && (
              <div className="mb-3">
                <label className="text-orange-300 text-[10px] font-bold mb-1.5 block uppercase tracking-wider flex items-center gap-2">
                  🎵 Select Track
                  {currentPlaylist && (
                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-orange-500/20 border border-orange-400/50 text-orange-100 font-semibold">
                      {currentPlaylist.source === 'admin' ? 'Your Uploads' : 'Admin Playlists'}
                    </span>
                  )}
                </label>
                <select
                  value={selectedPlaylistId || ''}
                  onChange={(e) => setSelectedPlaylistId(e.target.value)}
                  className="w-full backdrop-blur-md bg-gray-800/80 border-2 border-orange-500/50 text-white font-semibold rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
                >
                  {adminPlaylists.length > 0 && (
                    <optgroup label="Your Uploads">
                      {adminPlaylists.map((playlist) => (
                        <option key={playlist.id} value={playlist.id}>
                          {playlist.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {userPlaylists.length > 0 && (
                    <optgroup label="Admin Playlists">
                      {userPlaylists.map((playlist) => (
                        <option key={playlist.id} value={playlist.id}>
                          {playlist.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-8 bg-gray-800/50 rounded-lg border-2 border-orange-500/30">
                <Loader2 className="w-6 h-6 animate-spin text-orange-400" />
              </div>
            ) : currentPlaylist ? (
              <>
                {/* ✅ SINGLE iframe that NEVER unmounts - only innerHTML changes */}
                <div 
                  ref={iframeContainerRef}
                  className="rounded-lg overflow-hidden border-3 border-orange-600/40 shadow-lg"
                />
                {currentPlaylist.description && (
                  <p className="text-orange-200 text-[10px] mt-2 text-center font-medium">{currentPlaylist.description}</p>
                )}
              </>
            ) : (
              <div className="text-center py-8 bg-gray-800/50 rounded-lg border-2 border-orange-500/30">
                <Music className="w-8 h-8 text-orange-400/50 mx-auto mb-2" />
                <p className="text-orange-200 text-xs font-semibold">No Track Loaded</p>
              </div>
            )}
            
            {/* Disclaimer */}
            <div className="mt-3 pt-3 border-t-2 border-orange-600/30">
              <div className="backdrop-blur-md bg-yellow-500/20 border-2 border-yellow-400/60 rounded-lg p-3">
                <p className="text-yellow-100 text-[10px] text-center font-bold flex items-center justify-center gap-1.5 mb-1">
                  <span className="text-yellow-300 text-sm">🎵</span>
                  Third-Party Music
                </p>
                <p className="text-yellow-200/90 text-[9px] text-center font-medium leading-relaxed">
                  Music provided by external sources. All rights, licenses, and ownership belong to respective artists, labels, and platforms.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black rounded-2xl border-3 border-green-500/60 shadow-2xl w-full max-w-md">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 via-emerald-500 to-green-600 p-4 flex items-center justify-between border-b-3 border-green-700">
              <div className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-white" />
                <h3 className="text-white font-black text-sm tracking-wider">UPLOAD MUSIC</h3>
              </div>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-white hover:bg-green-600/50 h-7 w-7 rounded-lg border border-green-300/50 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div>
                <label className="text-green-300 text-xs font-bold mb-1.5 block uppercase tracking-wider">
                  Song Title *
                </label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  placeholder="Enter song title"
                  className="w-full backdrop-blur-md bg-gray-800/80 border-2 border-green-500/50 text-white font-semibold rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-400"
                />
              </div>

              <div>
                <label className="text-green-300 text-xs font-bold mb-1.5 block uppercase tracking-wider">
                  Artist Name *
                </label>
                <input
                  type="text"
                  value={uploadForm.artist}
                  onChange={(e) => setUploadForm({ ...uploadForm, artist: e.target.value })}
                  placeholder="Enter artist name"
                  className="w-full backdrop-blur-md bg-gray-800/80 border-2 border-green-500/50 text-white font-semibold rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-400"
                />
              </div>

              <div>
                <label className="text-green-300 text-xs font-bold mb-1.5 block uppercase tracking-wider">
                  Embed Code *
                </label>
                <textarea
                  value={uploadForm.embedCode}
                  onChange={(e) => setUploadForm({ ...uploadForm, embedCode: e.target.value })}
                  placeholder='Paste embed iframe from YouTube, Spotify, SoundCloud, etc.&#10;Example: <iframe src="..." ...></iframe>'
                  rows={4}
                  className="w-full backdrop-blur-md bg-gray-800/80 border-2 border-green-500/50 text-white font-mono text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-green-400 resize-none"
                />
                <p className="text-green-200/60 text-[10px] mt-1.5 font-medium">
                  💡 Get embed code from YouTube, Spotify, SoundCloud, or iHeartRadio
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-bold text-sm border-2 border-gray-600 transition-colors"
                  disabled={uploadLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploadLoading || !uploadForm.title || !uploadForm.artist || !uploadForm.embedCode}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold text-sm border-2 border-green-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {uploadLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload Music
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MusicPlayerDock;
